import { CRISP_WEBSITE_ID, IS_PROD } from "@/utilities/configurations";
import storageService from "@/services/middleware/storageService";
import { useRelationshipsStore } from "@/zustand/relationshipsStore";
import { useUsersStore } from "@/zustand/usersStore";
import { isIframe } from "@/utilities/constants";
import { userIsDemo } from "@/utilities/planHelpers";

type CrispCommand = [string, string, unknown?];

declare global {
  interface Window {
    $crisp?: CrispCommand[];
    CRISP_WEBSITE_ID?: string;
    CRISP_TOKEN_ID?: string;
  }
}

let crispScriptPromise: Promise<void> | null = null;

const loadCrispScript = (): Promise<void> =>
  (crispScriptPromise ??= new Promise<void>((resolve, reject) => {
    window.$crisp = window.$crisp || [];
    window.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;

    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  }));

/** Crisp only accepts a session-data value as string / number / boolean. */
const pushSessionData = (data: [string, string | number | boolean][]) => {
  const entries = data.filter(([, value]) => value !== "" && value != null);
  if (entries.length > 0) {
    window.$crisp?.push(["set", "session:data", [entries]]);
  }
};

const useCrisp = () => {
  const user = useUsersStore((state) => state.user);
  const { companyName, id, email, emailVerifiedAt } = user;
  const { relationships: allAvailableRelationships } = useRelationshipsStore(
    (state) => state
  );
  const isDev = IS_PROD !== "true";

  const startClient = async (planType: string): Promise<void> => {
    // Same rule the Intercom widget followed: never launch the chat outside
    // production, inside an iframe (embedded panel) or on a demo account.
    if (isDev || isIframe || userIsDemo(user)) return;

    // Must be set before the loader runs so the session is bound to the user.
    window.CRISP_TOKEN_ID = `${id}`;
    await loadCrispScript();

    const accountId = Number(
      "" + storageService.getItem("activeRelationshipId")
    );
    const account = allAvailableRelationships.find(
      (rel) => rel.id.toString() === accountId.toString()
    );

    if (email) window.$crisp?.push(["set", "user:email", [email]]);

    const nickname =
      companyName && !companyName.includes("@") ? companyName : undefined;
    if (nickname) window.$crisp?.push(["set", "user:nickname", [nickname]]);

    if (account?.companyName) {
      window.$crisp?.push(["set", "user:company", [account.companyName, {}]]);
    }

    pushSessionData([
      ["user_id", id],
      ["plan", planType],
      ["account_id", accountId || ""],
      ["account_name", account?.companyName ?? ""],
      ["created_at", emailVerifiedAt ?? ""],
    ]);
  };

  return { startClient };
};

export const shutdownCrisp = () => {
  window.$crisp?.push(["do", "session:reset"]);
};

export default useCrisp;
